import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from "@ngrx/store";
import { AppLocalStorage } from 'app/app-storage-manager/local-storage';
import { AppState } from "app/reducers";
import { MiscService } from 'app/shared/misc.service';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, exhaustMap, tap } from 'rxjs/operators';
import { AuthService } from '../_services/auth.service';
import { PosthogService } from 'app/shared/posthog.service';
import * as authActions from './auth.actions';

@Injectable({
  providedIn: 'root'
})
export class AuthEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.login),
      exhaustMap((action) =>
        this.authService.login(action.credentials).pipe(
          exhaustMap((response: any) => {
            if (response?.data?.otp_required) {
              return [
                authActions.otpRequired(),
                authActions.generateOtp({ credentials: action.credentials })
              ];
            }

            this.storageManager.storeToStorage('feature_permissions', response?.data?.data.feature_permissions);
            if (response.data.type == 'merchant') {
              if (response.data.data.activate) {
                localStorage.setItem('xs_access_token', response?.data?.token);
                localStorage.setItem('session_id', response?.data?.session_id);
                localStorage.setItem('user_email', action.credentials.email);
                this.storageManager.storeToStorage('user', response?.data?.data);
                this.storageManager.storeToStorage('user_type', response.data.type);
                if (response.data.data?.role === "admin" || response?.data?.data?.head_branch) {
                  this.storageManager.storeToStorage('admin', {
                    role: "global_admin",
                    merchantId: response.data.merchant_id
                  });
                }
                this.miscService.updateUser(response?.data?.data);
                this.posthog.identify(action.credentials.email, {
                  email: action.credentials.email,
                  merchant_id: response.data.merchant_id,
                  user_type: 'merchant',
                  role: response.data.data?.role,
                });
                this.router.navigate(['/app/dashboard']);
              }
              else {
                this.store.dispatch(authActions.userNotActivated({ response: response.data.data }));
              }
            } else if (response.data.type == 'user') {
              let permss = this.getUniquePermissions(response?.data?.user_data?.roles);
              localStorage.setItem('user-permissions', JSON.stringify(permss));
              this.storageManager.storeToStorage('user_type', response.data.type);
              localStorage.setItem('xs_access_token', response?.data?.token);
              localStorage.setItem('session_id', response?.data?.session_id);
              localStorage.setItem('user_email', action.credentials.email);
              this.storageManager.storeToStorage('user', response?.data?.data);
              this.miscService.updateUser(response?.data?.data);
              this.posthog.identify(action.credentials.email, {
                email: action.credentials.email,
                user_type: 'user',
              });
              this.router.navigate(['/app/dashboard']);
            }

            return [authActions.loginSuccessful()];
          }),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error?.error?.errors || 'Network error');
            return of(authActions.loginFailed({ message: error?.error?.errors, code: error.status }));
          })
        )
      )
    )
  );

  generateOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.generateOtp),
      exhaustMap((action) =>
        this.authService.generateOtp(action.credentials).pipe(
          exhaustMap(() => [authActions.generateOtpSuccess()]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error?.error?.errors || 'Unable to send OTP');
            return of(authActions.generateOtpFailed());
          })
        )
      )
    )
  );

  getUniquePermissions(roles: any[]): any[] {
    const uniquePermissionsMap = new Map<string, any>();

    roles.forEach((role) => {
      role.permissions.forEach((permission) => {
        Object.keys(permission).forEach((key) => {
          if (!uniquePermissionsMap.has(key)) {
            uniquePermissionsMap.set(key, permission[key]);
          }
        });
      });
    });

    return Array.from(uniquePermissionsMap.entries()).reduce((acc, [key, value]) => {
      acc.push({ [key]: value });
      return acc;
    }, []);
  }

  getAllPermissions(roles: any[]): any[] {
    let permissions: any[] = [];

    roles.forEach((role) => {
      permissions = [...permissions, ...role.permissions];
    });

    return [...new Set(permissions)];
  }

  fetchEntityTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.fetchEntityTypes),
      exhaustMap((action) =>
        this.authService.fetchEntityTypes(action.countryCode).pipe(
          exhaustMap((response: any) => [authActions.fetchEntityTypesSuccess({ entityTypes: response })]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.errors, 'An error occured');
            return of(authActions.fetchEntityTypesFailed());
          })
        )
      )
    )
  );

  fetchBusinessTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.fetchBusinessTypes),
      exhaustMap((action) =>
        this.authService.fetchBusinessTypes().pipe(
          exhaustMap((response: any) => [authActions.fetchBusinessTypesSuccess({ businessTypes: response.data })]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.errors, 'An error occured');
            return of(authActions.fetchBusinessTypesFailed());
          })
        )
      )
    )
  );
  fetchTransactionTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.fetchTransactionTypes),
      exhaustMap((action) =>
        this.authService.fetchTransactionTypes().pipe(
          exhaustMap((response: any) => [
            authActions.fetchTransactionTypesSuccess({ transactionTypes: response.data })
          ]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.errors, 'An error occured');
            return of(authActions.fetchTransactionTypesFailed());
          })
        )
      )
    )
  );
  createMerchant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.createMerchant),
      exhaustMap((action) =>
        this.authService.createMerchant(action.requestPayload).pipe(
          exhaustMap((response: any) => [authActions.createMerchantSuccess(response)]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.errors, 'An error occured');
            return of(authActions.createMerchantFailed());
          })
        )
      )
    )
  );

  requestPasswordReset = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.requestPasswordReset),
      exhaustMap((action) =>
        this.authService
          .requestPasswordRequest(action.payload)
          .pipe(
            tap((response: any) => {
              this.toastr.success(response.message);
              sessionStorage.setItem("resetPassId", action.payload.email)
            }),
            exhaustMap((response: any) => [
              authActions.requestPasswordResetSuccess(response),
            ]),
            catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(authActions.requestPasswordResetFailed())
            }
            )
          )
      )
    )
  )

  resetPassword = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.resetPassword),
      exhaustMap((action) =>
        this.authService
          .resetPassword(action.payload)
          .pipe(
            tap((response: any) => {
              this.toastr.success(response.message);
              sessionStorage.removeItem("resetPassId")
              this.router.navigateByUrl("/auth")
            }),
            exhaustMap((response: any) => [
              authActions.resetPasswordSuccess(response),
            ]),
            catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(authActions.resetPasswordFailed())
            }
            )
          )
      )
    )
  )

  fetchCompanyProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.fetchCompanyProfile),
      exhaustMap((action) =>
        this.authService.getCompanyProfile(action.companyNumber).pipe(
          exhaustMap((response: any) => [authActions.fetchCompanyProfileSuccess({ companyProfile: response.data })]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.info(error.error?.message, 'Could not validate incorporation number on company house');
            return of(authActions.fetchCompanyProfileFailed());
          })
        )
      )
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.logout),
      tap(() => {
        this.posthog.reset();
        localStorage.clear();
        this.router.navigate(['/auth']);
      }),
      exhaustMap(() => [])
    )
  );
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private storageManager: AppLocalStorage,
    private toastr: ToastrService,
    private miscService: MiscService,
    private router: Router,
    private store: Store<AppState>,
    private posthog: PosthogService,
  ) { }
}
